import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/api/axios";
import { API, fmtDate, ym } from "@/App";
import { Check, X } from "lucide-react";
import { confirmToast } from "../utils/ConfirmToast";
import { toast } from "react-hot-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
// Shadcn UI & Icon Imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  CircleDollarSign,
  CalendarDays,
  Briefcase,
  UserCheck,
  Hash,
  Pencil,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import BigCalendar from "@/components/calendar/BigCalendar";
import { addDays, startOfSecond } from "date-fns";
import CalendarHrPage from "../pages/CalenderHr";
import { MonthPicker } from "@/components/month-picker";

// --- Helper Functions ---

// Formats a date string to DD-MMM-YYYY (e.g., 23-OCT-2024)
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date
    .toLocaleDateString("en-IN", options)
    .replace(/ /g, "-")
    .toUpperCase();
};

// Formats a number to INR currency format (e.g., ₹ 750)
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(amount);
};

// --- Component ---

export default function PersonDetails() {
  const { uid } = useParams();
  const [stats, setStats] = useState(null); // Use a proper type/interface in a real app
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(ym(new Date()));
  const [balances, setBalances] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ holidays: [], leaves: [], events: [] });

  const [salaryRecords, setSalaryRecords] = useState([]);
  const [salaryPreviewRecords, setSalaryPreviewRecords] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);

  // Autofill timesheet modal state
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [autofillHours, setAutofillHours] = useState(200);
  const [autofillLoading, setAutofillLoading] = useState(false);

  const handleAutofillSave = async () => {
    const hours = Number(autofillHours) || 0;
    if (!uid) {
      toast.error("No user selected");
      return;
    }
    setAutofillLoading(true);
    try {
      await api.post(`${API}/time-entries/autofill-month`, {
        user_id: uid,
        month,
        hours,
        overwrite: false,
      });
      toast.success(`Autofill applied: ${hours} hours`);
      setAutofillOpen(false);
      // refresh view data
      load();
      fetchLatestSalary();
    } catch (err) {
      console.error("Autofill failed:", err);
      toast.error(err?.response?.data?.message || "Failed to apply autofill");
    } finally {
      setAutofillLoading(false);
    }
  };

  const previewSalary = async () => {
    if (!uid) return;
    setSalaryLoading(true);

    try {
      const response = await api.get(`${API}/users/salary/preview`, {
        params: { user_id: uid, month },
      });

      const salaryData = response.data;
      setSalaryPreviewRecords(salaryData);
      toast.success("Salary fetched successfully!");

      if (Array.isArray(salaryData) && salaryData.length > 0) {
        // Extract headers from the first object
        const headers = Object.keys(salaryData[0]);

        // Build CSV rows
        const csvRows = [
          headers.join(","), // header row
          ...salaryData.map((row) =>
            headers
              .map((header) => {
                const val = row[header];
                // escape commas, quotes, newlines properly
                if (val === null || val === undefined) return "";
                const safe = String(val).replace(/"/g, '""');
                return `"${safe}"`;
              })
              .join(","),
          ),
        ];

        // Join all rows with newlines
        const csvContent = csvRows.join("\n");

        // Create a downloadable CSV file
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `salary_preview_${stats.user.name}_${month}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Failed to fetch salary:", err);
      toast.error(err?.response?.data?.message || "Could not fetch salary.");
    } finally {
      setSalaryLoading(false);
    }
  };
  const generateSalary = async () => {
    if (!uid) return;
    setSalaryLoading(true);
    try {
      const response = await api.get(`${API}/users/salary`, {
        params: { user_id: uid, month },
      });
      setSalaryRecords([response.data.data]);
      toast.success("Salary fetched successfully!");
    } catch (err) {
      console.error("Failed to fetch salary:", err);
      toast.error(err?.response?.data?.message || "Could not fetch salary.");
    } finally {
      setSalaryLoading(false);
    }
  };
  const fetchLatestSalary = async () => {
    if (!uid || !month) return;
    try {
      const response = await api.get(`${API}/users/salary/latest/${uid}/`);
      console.log("Salary Response --->", response);
      if (response.data.data) {
        setSalaryRecords(response.data.data);
      } else {
        setSalaryRecords([]);
      }
    } catch (err) {
      console.error("Failed to fetch latest salary:", err);
      toast.error(err?.response?.data?.message || "Could not fetch salary.");
    }
  };

  const refresh = async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${API}/users/${uid}/stats`);
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch user stats:", err);
      setError("Could not load user details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  const handleLeaveDecision = async (leaveId, decision) => {
    if (!uid) return;

    confirmToast(
      `Are you sure you want to ${decision === "APPROVED" ? "approve" : "reject"} this leave?`,
      async () => {
        try {
          await api.post(`${API}/calendar/leave-requests/${leaveId}/decision`, {
            decision,
            approver_id: stats.user.id,
            approval_justification:
              decision === "REJECTED" ? "Not approved" : "approved",
          });

          // Reload data after decision
          refresh();
          load();

          toast.success(
            `Leave ${decision === "APPROVED" ? "approved" : "rejected"} successfully`,
          );
        } catch (err) {
          console.error("Failed to update leave status:", err);
          toast.error(
            err?.response?.data?.message || "Could not update leave request.",
          );
        }
      },
    );
  };
  // const handleLeaveDecision = async (leaveId, decision) => {
  //     if (!uid) return;

  //     try {
  //         await api.post(`${API}/calendar/leave-requests/${leaveId}/decision`, {
  //             decision,
  //             approver_id: stats.user.id,
  //             approval_justification: decision === "REJECTED" ? "Not approved" : "",
  //         });

  //         refresh();
  //         load();
  //     } catch (err) {
  //         console.error("Failed to update leave status:", err);
  //         alert(err?.response?.data?.message || "Could not update leave request.");
  //     }
  // };

  // --- Dynamic Badge Variants ---
  const getStatusVariant = (status) => {
    switch (status?.toUpperCase()) {
      case "IN PROGRESS":
        return "default";
      case "IN REVIEW":
        return "secondary";
      case "DONE":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getPriorityVariant = (priority) => {
    switch (priority?.toUpperCase()) {
      case "HIGH":
        return "destructive";
      case "MEDIUM":
        return "secondary";
      case "LOW":
        return "outline";
      default:
        return "outline";
    }
  };

  const getLeaveStatusVariant = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "default";
      case "PENDING":
        return "secondary";
      case "REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const load = async () => {
    const d = await api
      .get(`${API}/calendar`, { params: { month } })
      .then((x) => x.data);
    setData(d);
    if (uid) {
      const b = await api
        .get(`${API}/calendar/leave/balances`, { params: { user_id: uid } })
        .then((x) => x.data)
        .catch(() => null);
      setBalances(b);
    }
  };

  const calendarEvents = useMemo(() => {
    const holidays = data.holidays.map((h) => ({
      title: h.name,
      start: new Date(h.date),
      end: new Date(h.date),
      allDay: true,
      resource: { ...h, isHoliday: true },
    }));

    const events = data.events.map((ev) => ({
      title: ev.title,
      start: new Date(ev.date),
      end: new Date(ev.date),
      allDay: true,
      resource: { ...ev, isEvent: true },
    }));

    const leaves = data.leaves.map((l) => ({
      title: `${l.type} Leave`,
      start: new Date(l.start_date),
      end: addDays(new Date(l.end_date), 1),
      allDay: true,
      resource: { ...l, isLeave: true },
    }));

    return [...holidays, ...events, ...leaves];
  }, [data]);

  useEffect(() => {
    load();
    refresh();
    fetchLatestSalary(); // <-- fetch latest salary initially
  }, [uid, month]);


  console.log("States ===>",stats);
  const sortedRecords = [...salaryRecords].sort((a, b) =>
    b.month.localeCompare(a.month),
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-muted-foreground">
        Loading user details...
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex justify-center items-center h-screen text-destructive">
        {error || "User not found."}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Page Header */}
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Avatar className="h-20 w-20">
            <AvatarImage
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${stats.user.name}`}
              alt={stats.user.name}
            />
            <AvatarFallback className="text-xl">
              {stats.user.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {stats.user.name}
            </h1>
            <p className="text-muted-foreground">{stats.user.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline">{stats.user.role}</Badge>
              <Badge variant={stats.user.is_active ? "default" : "secondary"}>
                {stats.user.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>
        <div className="column gap-5">
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="column gap-5">
              {/*<Button variant="outline"><Pencil className="mr-2 h-4 w-4" /> Edit Profile</Button>*/}
              <div className="controls">
                <Label>Month</Label>
                <MonthPicker value={month} onChange={setMonth} />
              </div>
            </div>
            {/*<div className="column gap-5">*/}
            {/*    <Button variant="destructive"><Trash2 className="mr-2 h-4 w-8" /> Deactivate</Button>*/}

            {/*</div>*/}
          </div>
        </div>
      </header>

      <Separator />
      <div className="flex justify-between items-center mb-4">
        <Button
          variant="outline"
          onClick={generateSalary}
          loading={salaryLoading}
          className="hover:bg-blue-600 hover:text-white"
        >
          Generate Salary
        </Button>

        {/* Autofill Timesheet Dialog (admin only) */}
        {stats?.user?.role?.toUpperCase() === "ADMIN" && (
          <Dialog
            open={autofillOpen}
            onOpenChange={(v) => {
              setAutofillOpen(v);
              if (v) setAutofillHours(200);
            }}
          >
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="hover:bg-blue-600 hover:text-white"
              >
                AutofillTimesheet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Autofill Timesheet</DialogTitle>
              </DialogHeader>

              <div className="grid gap-2 mb-10">
                <Label>Hours</Label>
                <Input
                  type="number"
                  min={0}
                  value={autofillHours}
                  onChange={(e) => setAutofillHours(Number(e.target.value))}
                />
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setAutofillOpen(false)}
                  disabled={autofillLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleAutofillSave} disabled={autofillLoading}>
                  {autofillLoading ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <Button
          variant="outline"
          onClick={previewSalary}
          loading={salaryLoading}
          className="hover:bg-blue-600 hover:text-white"
        >
          Preview Salary
        </Button>
      </div>
      {/* Main Content Grid */}
      {/* <BigCalendar events={calendarEvents} /> */}
      <CalendarHrPage userId={uid} month={month} setMonth={setMonth} />

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Salary Records</CardTitle>
          <CardDescription>
            Salary generated (This does not reflect in the direct cost of a
            product. its calculated based on the total hours worked and the
            hourly rate).
          </CardDescription>
          <p style={{ color: "orange" }}>
            *Note: Even though you are shown rounded hours, the actual salaries
            are calculated based on the precision of the hours worked.
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Total Hours</TableHead>
                <TableHead>Hourly Rate</TableHead>
                <TableHead>Salary Amount</TableHead>
                {/* <TableHead>Date Generated</TableHead> */}
                <TableHead>Designation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* {salaryRecords.map((s, index) => (
                <TableRow key={index}>
                  <TableCell>{s.month}</TableCell>
                  <TableCell>{s.total_hours.toFixed(0)}</TableCell>
                  <TableCell>{formatCurrency(s.hourly_rate)}</TableCell>
                  <TableCell>{formatCurrency(s.salary_amount)}</TableCell>
                  <TableCell>{formatDate(s.generated_at)}</TableCell>
                </TableRow>
              ))} */}
              {sortedRecords.map((s, index) => (
                <TableRow key={index}>
                  <TableCell>{s.month}</TableCell>
                  <TableCell>{s.total_hours.toFixed(0)}</TableCell>
                  <TableCell>{formatCurrency(s.hourly_rate)}</TableCell>
                  <TableCell>{formatCurrency(s.salary_amount)}</TableCell>
                  {/* <TableCell>{formatDate(s.generated_at)}</TableCell> */}
                  <TableCell>{stats?.user?.role}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <main className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (Wider) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tasks Card */}
          <Card>
            <CardHeader>
              <CardTitle>Assigned Tasks</CardTitle>
              <CardDescription>
                Recent activity and assigned work.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Due Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.tasks && stats.tasks.length > 0 ? (
                    stats.tasks.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.title}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusVariant(t.status)}>
                            {t.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityVariant(t.priority)}>
                            {t.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(t.due_date)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center">
                        No tasks assigned.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Leave Requests Card */}
          <Card>
            <CardHeader>
              <CardTitle>Leave History</CardTitle>
              <CardDescription>All submitted leave requests.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Approve/Decline</TableHead>
                  </TableRow>
                </TableHeader>
                {/* <TableBody>
                                    {stats.leave_requests && stats.leave_requests.length > 0 ? (
                                        stats.leave_requests.map((lr) => (
                                            <TableRow key={lr.id}>
                                                <TableCell className="font-medium">{lr.type}</TableCell>
                                                <TableCell>{formatDate(lr.start_date)}</TableCell>
                                                <TableCell>{formatDate(lr.end_date)}</TableCell>
                                                <TableCell><Badge variant={getLeaveStatusVariant(lr.status)}>{lr.status}</Badge></TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center">No leave requests found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody> */}
                {/* <TableBody>
                                    {stats.leave_requests && stats.leave_requests.length > 0 ? (
                                        stats.leave_requests.map((lr) => (
                                            <TableRow key={lr.id}>
                                                <TableCell className="font-medium">{lr.type}</TableCell>
                                                <TableCell>{formatDate(lr.start_date)}</TableCell>
                                                <TableCell>{formatDate(lr.end_date)}</TableCell>
                                                <TableCell>
                                                    <Badge variant={getLeaveStatusVariant(lr.status)}>{lr.status}</Badge>
                                                </TableCell>
                                                <TableCell className="flex gap-2">
                                                    {lr.status === "PENDING" && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="default"
                                                                onClick={() => handleLeaveDecision(lr.id, "APPROVED")}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="destructive"
                                                                onClick={() => handleLeaveDecision(lr.id, "REJECTED")}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} className="h-24 text-center">
                                                No leave requests found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody> */}
                <TableBody>
                  {stats.leave_requests && stats.leave_requests.length > 0 ? (
                    stats.leave_requests.map((lr) => (
                      <TableRow key={lr.id}>
                        <TableCell className="font-medium">{lr.type}</TableCell>
                        <TableCell>{formatDate(lr.start_date)}</TableCell>
                        <TableCell>{formatDate(lr.end_date)}</TableCell>
                        <TableCell>
                          <Badge variant={getLeaveStatusVariant(lr.status)}>
                            {lr.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex gap-2">
                          {lr.status === "PENDING" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:bg-green-100"
                                title="Approve Leave"
                                onClick={() =>
                                  handleLeaveDecision(lr.id, "APPROVED")
                                }
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-100"
                                title="Reject Leave"
                                onClick={() =>
                                  handleLeaveDecision(lr.id, "REJECTED")
                                }
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        No leave requests found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Right Column (Narrower) */}
        <div className="space-y-6">
          {/* Person Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-center">
                <Briefcase className="mr-3 h-4 w-4 text-muted-foreground" />{" "}
                <span>
                  Job Title: <strong>{stats.user.job_title}</strong>
                </span>
              </div>
              <div className="flex items-center">
                <CalendarDays className="mr-3 h-4 w-4 text-muted-foreground" />{" "}
                <span>
                  Joined on: <strong>{formatDate(stats.user.join_date)}</strong>
                </span>
              </div>
              <Separator />
              <div className="flex items-center text-red-600">
                <ShieldAlert className="mr-3 h-4 w-4" />{" "}
                <span>Admin Confidential</span>
              </div>
              <div className="flex items-center">
                <CircleDollarSign className="mr-3 h-4 w-4 text-muted-foreground" />{" "}
                <span>
                  Hourly Rate:{" "}
                  <strong>{formatCurrency(stats.user.hourly_rate)}</strong>
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Hourly Rate History Card */}
          <Card>
            <CardHeader>
              <CardTitle>Hourly Rate History</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.user.hourly_rate_history &&
              stats.user.hourly_rate_history.length > 0 ? (
                <div className="space-y-2">
                  {stats.user.hourly_rate_history
                    .slice()
                    .reverse()
                    .map((h, i) => (
                      <div
                        key={h._id || h.id || i}
                        className="flex justify-between items-center text-sm border-b pb-2 last:border-0"
                      >
                        <span>{formatCurrency(h.rate)}</span>
                        <span className="text-muted-foreground text-xs">
                          {/* The date the rate started applying, which for a
                              back-dated change is not when it was entered.
                              "0000-01-01" is the sentinel for a rate with no
                              known start, so it reads as "from the start". */}
                          {!h.effective_from
                            ? formatDate(h.changed_at)
                            : h.effective_from.startsWith("0000")
                              ? "from the start"
                              : `from ${formatDate(h.effective_from)}`}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No history available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Leave Balance Card */}
          <Card>
            <CardHeader>
              <CardTitle>Leave Balance</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              {Object.entries(stats?.leave_balance?.balances || []).map(
                ([type, balance]) => (
                  <div
                    key={type}
                    className="flex flex-col items-center justify-center p-3 bg-muted rounded-md"
                  >
                    <span className="text-2xl font-bold">{balance}</span>
                    <span className="text-xs font-medium uppercase text-muted-foreground">
                      {type}
                    </span>
                  </div>
                ),
              )}
            </CardContent>
          </Card>

          {/* Qualifications Card */}
          <Card>
            <CardHeader>
              <CardTitle>Qualifications</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {stats.user.qualifications.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
